/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { IconX, IconUpload } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  useFetchOrdersData,
  useFetchOrderTemplate,
  useOrderBulkDocMutation,
} from "../../../apis/orderApis";
import toast from "react-hot-toast";

type OrderBulkUploadModalsProps = {
  onClose: () => void;
  onResponseOpene: () => void;
  setOrderBulkResponse: React.Dispatch<React.SetStateAction<any>>;
  isSidebar?: boolean;
};

export default function OrderBulkUploadModals({
  onClose,
  onResponseOpene,
  setOrderBulkResponse,
  isSidebar = false,
}: OrderBulkUploadModalsProps) {
  const [file, setFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState("All");
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const { mutate: uploadDocMutate, isPending: isBulkUploading } =
    useOrderBulkDocMutation();
  const { data: templateData, refetch: fetchTemplates } =
    useFetchOrderTemplate();

  const {
    data: orders,
    isPending: listPending,
    refetch,
  } = useFetchOrdersData(pageNo, searchFor, limit);

  const handleUploadDoc = () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    uploadDocMutate(
      { orders: file },
      {
        onSuccess: (data: any) => {
          setOrderBulkResponse(data.data);
          onClose();
          onResponseOpene();
          refetch();
          toast.success("Orders uploaded successfully");
        },
        onError: (error: any) => {
          console.log(error, "error");
          toast.error(`${error}`);
        },
      }
    );
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Staff Template");

    // Row 1: Info
    const infoRow = worksheet.addRow([
      "This template is for importing order data. Fields in red are mandatory; others are optional. Please do not modify the first 3 rows.",
    ]);
    worksheet.mergeCells(`A1:R1`);
    infoRow.eachCell((cell:any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFD9" },
      };
      cell.font = {
        name: "Arial",
        size: 10,
        bold: true,
        color: { argb: "212121" },
      };
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } }, // black
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });

    infoRow.height = 25; // height in points

    // Row 2: Validation row
    const validationRow = worksheet.addRow([
      "Client Id is required",
      "Reference Number is required",
      "Full name is required",
      "Receiver Phone (10–12 digits) is required",
      "Receiver Email is required",
      "Receiver Country.",
      "Receiver City  is required",
      "Receiver District",
      "Receiver Address is required",
      "Receiver National Address  is required",
      "Shipment Cost is required",
      "Payment Type (CC, COD) is required",
      "Cod Amount is required",
      "Number of Pieces is required",
      "Weight is required",
      "Service is required",
      "Shipment Type is required",
      "Description Type is required",
    ]);
    validationRow.eachCell((cell:any) => {
      let fillColor = "FFCCCC"; // default light red

      if (Number(cell.col) === 5) {
        fillColor = "FFEB9C"; // light yellow
      }
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fillColor },
      };

      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };

      cell.font = { color: { argb: "212121" } };

      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } }, // black
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    validationRow.height = 60; // height in points

    // Row 3: Headings
    const headings = Object.keys(templateData[0] || {}); // dynamic
    const headingsRow = worksheet.addRow(headings);

    headingsRow.eachCell((cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "217447" },
      };
      cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } }, // black
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    headingsRow.height = 25; // height in points

    // Optional: adjust column widths dynamically
    worksheet.columns = headings.map(() => ({ width: 25 }));

    // Adjust column widths
    worksheet.columns = [
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
    ];

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "order-template.xlsx");
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const content = (
    <div className={`flex flex-col h-full ${isSidebar ? "bg-transparent p-0" : "bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC]"}`}>
      {isSidebar ? (
        <div className="space-y-4 px-6 py-6 sm:px-4 pb-4">
          <div className="flex justify-between items-center mb-2">
            <p className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">Bulk Order</p>
          </div>

          <div className="relative z-10 space-y-4 pt-1">
            <FileUploader
              name="csvFile"
              extraParentClasses="col-span-3"
              img="/img/upload-img.png"
              title="Upload CSV File"
              message="Upload a CSV file only (Max size: 2 MB)."
              classNameMain="bg-transparent border-none flex items-center gap-3"
              fileVal={file}
              setFileVal={(file: any) => {
                setFile(file);
              }}
              showPreview={true}
              uploadType="document"
              required
            />

            {file && (
              <p className="text-xs text-green-600 font-bold">
                Selected File: {file.name}
              </p>
            )}

            <ul className="list-disc px-4 text-[11px] text-[#212121]/70 font-semibold space-y-1">
              <li>Download the Excel/CSV template before uploading.</li>
              <li>Do not change column headers.</li>
              <li>Mandatory fields: Sender, Receiver, Reference, etc.</li>
            </ul>

            <div className="flex flex-wrap items-center justify-between gap-4 bg-yellow-50 px-4 py-2.5 rounded-md border border-[#D2D2D2]/50 mt-4">
              <p className="text-xs text-[#212121] font-medium">
                <b>Note</b> : Please upload the file in the same format as the downloaded file.
              </p>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="bg-[#000000] text-white flex items-center space-x-2 px-3 py-2 rounded-md text-xs font-semibold whitespace-nowrap hover:bg-black/90 transition-colors disabled:opacity-60 shrink-0 cursor-pointer"
              >
                <img src="/img/csvxl.svg" alt="" className="w-4 h-4" />
                <span>Download</span>
              </button>
            </div>

            <button
              id="order-bulk-submit-btn"
              onClick={handleUploadDoc}
              disabled={isBulkUploading}
              className="hidden"
            >
              Submit
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center flex-shrink-0">
            <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px]">
              <h2 className="text-2xl font-bold">Bulk Upload Orders</h2>
            </div>
            <div className="relative">
              <button
                onClick={onClose}
                className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="w-full py-4 px-6 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4">
              <FileUploader
                name="csvFile"
                extraParentClasses="col-span-3"
                img="/img/upload-img.png"
                title="Upload CSV File"
                message="Upload a CSV file only (Max size: 2 MB)."
                classNameMain="bg-transparent border-none flex items-center gap-3"
                fileVal={file}
                setFileVal={(file: any) => {
                  setFile(file);
                }}
                showPreview={true}
                uploadType="document"
                required
              />

              {file && (
                <p className="text-sm text-green-600 font-medium">
                  Selected File: {file.name}
                </p>
              )}

              <ul className="list-disc px-5 text-sm text-[#1E293B] font-medium space-y-1">
                <li>Download the Excel/CSV template before uploading.</li>
                <li>Do not change column headers.</li>
                <li>Mandatory fields: Sender, Receiver, Reference, etc.</li>
              </ul>

              <div className="flex items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border-[#D2D2D2]">
                <p className="text-xs text-[#212121] font-medium">
                  <b>Note</b> : Please upload the file in the same format as the
                  downloaded file.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="bg-[#000000] text-white flex items-center space-x-1 p-2 rounded-md text-sm"
                >
                  <img src="/img/csvxl.svg" alt="" />
                  <span>Download</span>
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px] flex-shrink-0">
            <button
              onClick={handleUploadDoc}
              disabled={isBulkUploading}
              className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] flex items-center rounded-full p-2 px-6 space-x-1 disabled:cursor-not-allowed text-sm font-medium"
            >
              {isBulkUploading ? "Uploading..." : "Submit"}
            </button>
          </div>
        </>
      )}
    </div>
  );

  if (isSidebar) {
    return content;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="rounded-2xl max-w-[700px] w-full mx-4 flex flex-col relative overflow-hidden bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] shadow-2xl">
        {content}
      </div>
    </div>
  );
}
